import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Plus, FileText, Trash2, Loader2, ArrowRight, Copy } from "lucide-react";

const DURATION_LABEL = {
    "60s": "60s",
    "90s": "90s",
    "2m30": "2:30",
    "3m": "3:00",
};

const ClientDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const confirm = useConfirm();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [usage, setUsage] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: projs }, { data: u }] = await Promise.all([
                api.get("/projects"),
                api.get("/me/usage"),
            ]);
            setProjects(projs);
            setUsage(u);
        } catch (e) {
            toast.error("Failed to load projects");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const createProject = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const { data } = await api.post("/projects", {
                name: newName.trim() || "Untitled Project",
            });
            toast.success("Project created");
            setShowModal(false);
            setNewName("");
            navigate(`/projects/${data.id}/intake`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to create project");
        } finally {
            setCreating(false);
        }
    };

    const deleteProject = async (id, name) => {
        const ok = await confirm({
            title: "Delete Project",
            message: `Delete “${name}” and every script version inside it? This can’t be undone.`,
            confirmLabel: "Delete Project",
            cancelLabel: "Keep Project",
            variant: "danger",
        });
        if (!ok) return;
        try {
            await api.delete(`/projects/${id}`);
            toast.success("Project deleted");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const duplicateProject = async (id) => {
        try {
            const { data } = await api.post(`/projects/${id}/duplicate`);
            toast.success("Project duplicated");
            navigate(`/projects/${data.id}`);
        } catch {
            toast.error("Duplicate failed");
        }
    };

    const requiredIntakeKeys = [
        "video_purpose",
        "video_platforms",
        "video_topic",
        "duration",
        "video_pov",
        "video_myth",
        "video_belief_shift",
        "desired_cta",
    ];
    const hasValue = (v) => {
        if (v === null || v === undefined) return false;
        if (typeof v === "string") return v.trim().length > 0;
        if (Array.isArray(v)) return v.length > 0;
        return Boolean(v);
    };
    const intakeComplete = (p) => {
        const intake = p.intake || {};
        return requiredIntakeKeys.every((k) => hasValue(intake[k]));
    };

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="client-dashboard">
            {user?.access_expires_at && (() => {
                const ms = new Date(user.access_expires_at).getTime() - Date.now();
                const daysLeft = Math.ceil(ms / (1000 * 60 * 60 * 24));
                const expiryLabel = new Date(user.access_expires_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
                let bannerClass = "bg-un-lime border-un-onyx text-un-onyx";
                let statusText = `${daysLeft} Days Of Access Remaining`;
                if (daysLeft < 0) {
                    bannerClass = "bg-red-600 border-red-800 text-white";
                    statusText = "Your Access Has Expired. Contact Your Admin To Renew.";
                } else if (daysLeft <= 3) {
                    bannerClass = "bg-red-500 border-red-700 text-white";
                    statusText = `Only ${daysLeft} Day${daysLeft === 1 ? "" : "s"} Of Access Left`;
                } else if (daysLeft <= 14) {
                    bannerClass = "bg-un-pumpkin border-un-onyx text-un-onyx";
                    statusText = `${daysLeft} Days Of Access Remaining`;
                }
                return (
                    <div
                        className={`border-2 px-5 py-4 mb-8 flex flex-wrap items-center justify-between gap-3 ${bannerClass}`}
                        data-testid="access-banner"
                    >
                        <div className="un-mono text-sm font-bold">
                            {statusText}
                        </div>
                        <div className="un-mono text-xs opacity-90">
                            Access Expires <span className="font-bold">{expiryLabel}</span>
                        </div>
                    </div>
                );
            })()}
            {usage && !usage.unlimited && (() => {
                const used = usage.scripts_this_month;
                const lim = usage.monthly_script_limit;
                const pct = Math.min(100, (used / lim) * 100);
                const over = used >= lim;
                const barBg = over ? "bg-red-600" : pct >= 80 ? "bg-un-pumpkin" : "bg-un-berry";
                return (
                    <div className="border-2 border-un-onyx bg-white p-4 mb-8" data-testid="usage-meter">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                            <div className="un-mono text-sm font-bold">
                                Monthly Generations: <span className={over ? "text-red-600" : ""}>{used}</span> / {lim}
                            </div>
                            <div className="un-mono text-xs text-un-onyx/70">
                                {over ? "Limit Reached · Contact Admin To Raise It" : `${lim - used} Generation${lim - used === 1 ? "" : "s"} Remaining This Month`}
                            </div>
                        </div>
                        <div className="w-full h-2 border-2 border-un-onyx bg-white">
                            <div className={`h-full ${barBg}`} style={{ width: `${pct}%` }} />
                        </div>
                    </div>
                );
            })()}
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
                <div>
                    <div className="un-label text-un-grape mb-3">Workspace · {user?.name}</div>
                    <h1 className="un-h1 text-5xl md:text-6xl">
                        Your <span className="text-un-berry">Projects</span>.
                    </h1>
                    <p className="text-lg text-un-onyx/70 mt-3 max-w-2xl">
                        Each project holds one intake and every script variant generated from it.
                    </p>
                </div>
                <button
                    className="un-btn-primary text-base flex items-center gap-2"
                    onClick={() => setShowModal(true)}
                    data-testid="new-project-button"
                >
                    <Plus className="w-4 h-4" /> New script project
                </button>
            </div>

            {loading ? (
                <div className="un-card p-12 flex items-center justify-center gap-3" data-testid="projects-loading">
                    <Loader2 className="w-5 h-5 animate-spin text-un-berry" />
                    <span className="un-label text-un-grape">Loading projects…</span>
                </div>
            ) : projects.length === 0 ? (
                <div className="un-card p-12 text-center" data-testid="empty-projects">
                    <FileText className="w-8 h-8 mx-auto mb-4 text-un-berry" />
                    <div className="un-h3 text-2xl mb-2">No projects yet</div>
                    <p className="text-un-onyx/70 mb-6">
                        Kick off your first script project. It takes about 3 minutes to walk through the intake.
                    </p>
                    <button className="un-btn-primary" onClick={() => setShowModal(true)} data-testid="empty-new-project-button">
                        Start your first project
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {projects.map((p) => {
                        const complete = intakeComplete(p);
                        return (
                        <div key={p.id} className="un-card un-card-hover p-8 flex flex-col min-h-[240px]" data-testid={`project-card-${p.id}`}>
                            <div className="flex items-start justify-between gap-3 mb-5">
                                <div className="un-label text-un-grape">Project</div>
                                <div className="flex items-center gap-2 flex-wrap justify-end">
                                    {!complete && (
                                        <span className="un-pill un-pill-berry whitespace-nowrap" data-testid={`project-incomplete-${p.id}`}>Intake · Resume</span>
                                    )}
                                    {p.duration && (
                                        <span className="un-pill un-pill-outline whitespace-nowrap">{DURATION_LABEL[p.duration] || p.duration}</span>
                                    )}
                                    <span className="un-pill un-pill-lime whitespace-nowrap">{p.script_count} generation{p.script_count === 1 ? "" : "s"}</span>
                                </div>
                            </div>
                            <div className="un-h2 text-3xl leading-tight mb-3 break-words" data-testid={`project-name-${p.id}`}>
                                {p.name}
                            </div>
                            <div className="un-mono text-xs text-un-onyx/50 mb-8">
                                Updated {new Date(p.updated_at).toLocaleDateString()}
                            </div>
                            <div className="mt-auto flex items-center justify-between gap-2 pt-2 border-t-2 border-un-onyx/10">
                                <Link
                                    to={complete ? `/projects/${p.id}` : `/projects/${p.id}/intake`}
                                    className="un-btn-outline text-sm flex items-center gap-2"
                                    data-testid={`open-project-${p.id}`}
                                >
                                    {complete ? "Open" : "Resume Intake"} <ArrowRight className="w-3.5 h-3.5" />
                                </Link>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => duplicateProject(p.id)}
                                        aria-label="Duplicate project"
                                        title="Duplicate"
                                        className="un-btn-ghost !px-2 text-un-onyx/40 hover:!text-un-berry"
                                        data-testid={`duplicate-project-${p.id}`}
                                    >
                                        <Copy className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => deleteProject(p.id, p.name)}
                                        aria-label="Delete project"
                                        className="un-btn-ghost !px-2 text-un-onyx/40 hover:!text-un-berry"
                                        data-testid={`delete-project-${p.id}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}

            {showModal && (
                <div
                    className="fixed inset-0 bg-un-onyx/60 flex items-center justify-center p-4 z-40"
                    role="dialog"
                    aria-modal="true"
                    onClick={() => setShowModal(false)}
                >
                    <form
                        onSubmit={createProject}
                        className="un-card p-8 max-w-md w-full"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="new-project-modal"
                    >
                        <div className="un-label text-un-grape mb-4">New project</div>
                        <h2 className="un-h2 text-3xl mb-6">Name Your Script Project</h2>
                        <label className="block mb-6">
                            <span className="un-label text-un-onyx block mb-2">Project name</span>
                            <input
                                type="text"
                                autoFocus
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                className="un-input"
                                placeholder="e.g. Q1 Founder Series - Hook #1"
                                data-testid="new-project-name-input"
                            />
                        </label>
                        <div className="flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                className="un-btn-ghost"
                                data-testid="new-project-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={creating}
                                className="un-btn-primary flex items-center gap-2"
                                data-testid="new-project-create"
                            >
                                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                Create & start intake
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default ClientDashboard;
